import React, { useEffect } from 'react';

export type FloatingMessageType = 'error' | 'warning' | 'info' | 'success';

interface FloatingMessageBoxProps {
    message: string;
    title?: string;
    type?: FloatingMessageType;
    visible?: boolean;
    onClose?: () => void;
    autoHideMs?: number;
}

const typeClasses: Record<FloatingMessageType, { bg: string; border: string; text: string; icon: string }> = {
    error: {
        bg: 'bg-red-50',
        border: 'border-red-300',
        text: 'text-red-700',
        icon: 'error',
    },
    warning: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-300',
        text: 'text-yellow-800',
        icon: 'warning',
    },
    info: {
        bg: 'bg-pink-50',
        border: 'border-pink-300',
        text: 'text-pink-800',
        icon: 'info',
    },
    success: {
        bg: 'bg-green-50',
        border: 'border-green-300',
        text: 'text-green-700',
        icon: 'check_circle',
    },
};

const FloatingMessageBox: React.FC<FloatingMessageBoxProps> = ({
    message,
    title,
    type = 'info',
    visible = true,
    onClose,
    autoHideMs,
}) => {
    useEffect(() => {
        if (!visible || !onClose || !autoHideMs || autoHideMs <= 0) return;
        const timer = window.setTimeout(() => onClose(), autoHideMs);
        return () => window.clearTimeout(timer);
    }, [visible, onClose, autoHideMs]);

    if (!visible) return null;

    const classes = typeClasses[type];

    return (
        <div className="fixed left-1/2 top-20 z-[110] w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 pointer-events-none">
            <div
                role="alert"
                className={`${classes.bg} ${classes.border} ${classes.text} pointer-events-auto rounded-lg border shadow-lg px-4 py-3`}
            >
                <div className="flex items-start gap-3">
                    <span className="material-symbols-outlined text-xl leading-none mt-0.5">{classes.icon}</span>
                    <div className="flex-1 min-w-0">
                        {title && <p className="font-semibold">{title}</p>}
                        <p className="text-sm break-words">{message}</p>
                    </div>
                    {onClose && (
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Schließen"
                            className="text-current/70 hover:text-current transition-colors"
                        >
                            <span className="material-symbols-outlined text-lg leading-none">close</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FloatingMessageBox;
